import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useMemo,
} from 'react';

interface IRollbarIdContextType {
  rollbarId: string | null;
  setRollbarId: (id: string | null) => void;
}

const RollbarIdContext = createContext<IRollbarIdContextType>({
  rollbarId: null,
  setRollbarId: () => {
    throw new Error('useRollbarId must be used within a RollbarIdProvider');
  },
});

export const useRollbarId = () => {
  const context = useContext(RollbarIdContext);
  return context;
};

interface IRollbarIdProviderProps {
  children: ReactNode;
}

// Global reference to setRollbarId function so it can be called from errorLink
let globalSetRollbarId: ((id: string | null) => void) | null = null;

export const setGlobalRollbarId = (id: string | null) => {
  if (globalSetRollbarId) {
    globalSetRollbarId(id);
  }
};

export const RollbarIdProvider: React.FC<IRollbarIdProviderProps> = ({
  children,
}) => {
  const [rollbarId, setRollbarId] = useState<string | null>(null);

  useEffect(() => {
    // Set the global reference when provider mounts
    globalSetRollbarId = setRollbarId;

    // Clean up when provider unmounts
    return () => {
      globalSetRollbarId = null;
    };
  }, []);

  const value = useMemo(
    () => ({
      rollbarId,
      setRollbarId,
    }),
    [rollbarId]
  );

  return (
    <RollbarIdContext.Provider value={value}>
      {children}
    </RollbarIdContext.Provider>
  );
};
